import React, { useState, useEffect } from "react";
import { useOrder } from "../context/OrderContext";
import { useOrderSave } from "../context/OrderSaveContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import FloatingLabelInput from "../components/form_elements/FloatingLabelInput";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { showErrorToast, showSuccessToast } from "../components/toasts/ToastService";

const labels = [
    {
        key: "bokaboseg",
        label: "Bokabőség (cm)",
        left: "hmeliorder_bokaboseg_left",
        right: "hmeliorder_bokaboseg_right",
    },
    {
        key: "szarboseg",
        label: "Szárbőség (cm)",
        left: "hmeliorder_szarboseg_left",
        right: "hmeliorder_szarboseg_right",
    },
    {
        key: "szarmagassag",
        label: "Szármagasság (cm)",
        left: "hmeliorder_szarmagassag_left",
        right: "hmeliorder_szarmagassag_right",
    },
    {
        key: "ikraboseg",
        label: "Ikrabőség (cm)",
        left: "hmeliorder_ikraboseg_left",
        right: "hmeliorder_ikraboseg_right",
    },
];

const MeretvetelSzaras = () => {
    const { order, updateOrder } = useOrder();
    const { saveOrder } = useOrderSave();
    const navigate = useStepNavigate();

    useEffect(() => {
        if (order.hmeliorder?.hmeliorder_is_hightopshoes === 0 || order.hmeliorder?.hmeliorder_depot_prefix === "BETÉT") {
            navigate("/Emeles");
        }
    }, [order.hmeliorder?.hmeliorder_is_hightopshoes, navigate]);

    const [bal, setBal] = useState({
        bokaboseg: order.hmeliorder?.hmeliorder_bokaboseg_left || "",
        szarboseg: order.hmeliorder?.hmeliorder_szarboseg_left || "",
        szarmagassag: order.hmeliorder?.hmeliorder_szarmagassag_left || "",
        ikraboseg: order.hmeliorder?.hmeliorder_ikraboseg_left || "",
    });
    const [jobb, setJobb] = useState({
        bokaboseg: order.hmeliorder?.hmeliorder_bokaboseg_right || "",
        szarboseg: order.hmeliorder?.hmeliorder_szarboseg_right || "",
        szarmagassag: order.hmeliorder?.hmeliorder_szarmagassag_right || "",
        ikraboseg: order.hmeliorder?.hmeliorder_ikraboseg_right || "",
    });

    const [loading, setLoading] = useState(false);

    const handleInput = (side, key, value) => {
        if (/^\d{0,2}(\.\d{0,2})?$/.test(value)) {
            if (side === "bal") {
                let uj = { ...bal, [key]: value };

                // Ha a szármagasság értéke változik, és 20 alá kerül, töröljük az ikrabőség értékét
                if (key === "szarmagassag" && parseFloat(value) <= 20) {
                    uj = { ...uj, ikraboseg: "" };
                }

                setBal(uj);
                updateOrder({
                    ...order,
                    hmeliorder: {
                        ...order.hmeliorder,
                        [labels.find((l) => l.key === key)?.left]: value,
                        ...(key === "szarmagassag" && parseFloat(value) <= 20
                            ? { hmeliorder_ikraboseg_left: "" }
                            : {}),
                    },
                });
            } else {
                let uj = { ...jobb, [key]: value };

                // Ha a szármagasság értéke változik, és 20 alá kerül, töröljük az ikrabőség értékét
                if (key === "szarmagassag" && parseFloat(value) <= 20) {
                    uj = { ...uj, ikraboseg: "" };
                }

                setJobb(uj);
                updateOrder({
                    ...order,
                    hmeliorder: {
                        ...order.hmeliorder,
                        [labels.find((l) => l.key === key)?.right]: value,
                        ...(key === "szarmagassag" && parseFloat(value) <= 20
                            ? { hmeliorder_ikraboseg_right: "" }
                            : {}),
                    },
                });
            }
        }
    };

    const handleSaveAndNavigate = async (nextPath) => {
        try {
            setLoading(true);

            const updatedOrder = {
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    hmeliorder_bokaboseg_left: bal.bokaboseg,
                    hmeliorder_szarboseg_left: bal.szarboseg,
                    hmeliorder_szarmagassag_left: bal.szarmagassag,
                    hmeliorder_bokaboseg_right: jobb.bokaboseg,
                    hmeliorder_szarboseg_right: jobb.szarboseg,
                    hmeliorder_szarmagassag_right: jobb.szarmagassag,
                    hmeliorder_ikraboseg_left: bal.ikraboseg,
                    hmeliorder_ikraboseg_right: jobb.ikraboseg,
                },
            };

            updateOrder(updatedOrder);

            const result = await saveOrder(updatedOrder);
            if (result.success) {
                showSuccessToast("A rendelés sikeresen mentve!");
                navigate(nextPath);
            } else {
                showErrorToast("Hiba történt a rendelés mentésekor!");
            }
        } catch (error) {
            console.error("Hiba a rendelés mentésekor:", error);
            showErrorToast("Hiba történt a rendelés mentésekor!");
        } finally {
            setLoading(false);
        }
    };

    const handleEnter = (e, currentId, nextId) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (nextId) {
                const nextInput = document.getElementById(nextId);
                if (nextInput) {
                    nextInput.focus();
                }
            } else if (allFilled) {
                handleSaveAndNavigate("/Emeles");
            }
        }
    };

    const allFilled = labels.every((l) => {
        if (l.key === "ikraboseg") {
            return (
                (!bal.szarmagassag || parseFloat(bal.szarmagassag) > 20 ? bal[l.key] !== "" : true) &&
                (!jobb.szarmagassag || parseFloat(jobb.szarmagassag) > 20 ? jobb[l.key] !== "" : true)
            );
        }
        return bal[l.key] !== "" && jobb[l.key] !== "";
    });

    return (
        <TablerLayout title="Méretvétel (száras)">
            <div className="row justify-content-center">
                <div className="col-md-10">
                    <div className="card-body">
                        <div className="row g-4">
                            <div className="row">
                                <div className="col-12 col-md-6 mb-4 mb-md-0">
                                    <div className="text-center fw-bold fs-5 mb-3">Bal láb</div>
                                    {labels.map((l, idx) => (
                                        <div className="mb-3" key={l.key}>
                                            <FloatingLabelInput
                                                label={l.label}
                                                id={`bal-${l.key}`}
                                                name={`bal-${l.key}`}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="99"
                                                addon="cm"
                                                fontSize={25}
                                                value={bal[l.key]}
                                                disabled={
                                                    l.key === "ikraboseg" &&
                                                    (!bal.szarmagassag || parseFloat(bal.szarmagassag) <= 20)
                                                }
                                                onChange={(e) => handleInput("bal", l.key, e.target.value)}
                                                onKeyDown={(e) =>
                                                    handleEnter(
                                                        e,
                                                        `bal-${l.key}`,
                                                        idx < labels.length - 1
                                                            ? `bal-${labels[idx + 1].key}`
                                                            : "jobb-bokaboseg"
                                                    )
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="col-12 col-md-6">
                                    <div className="text-center fw-bold fs-5 mb-3">Jobb láb</div>
                                    {labels.map((l, idx) => (
                                        <div className="mb-3" key={l.key}>
                                            <FloatingLabelInput
                                                label={l.label}
                                                id={`jobb-${l.key}`}
                                                name={`jobb-${l.key}`}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="99"
                                                addon="cm"
                                                fontSize={25}
                                                value={jobb[l.key]}
                                                disabled={
                                                    l.key === "ikraboseg" &&
                                                    (!jobb.szarmagassag || parseFloat(jobb.szarmagassag) <= 20)
                                                }
                                                onChange={(e) => handleInput("jobb", l.key, e.target.value)}
                                                onKeyDown={(e) =>
                                                    handleEnter(
                                                        e,
                                                        `jobb-${l.key}`,
                                                        idx < labels.length - 1
                                                            ? `jobb-${labels[idx + 1].key}`
                                                            : null
                                                    )
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card-footer d-flex justify-content-between">
                        <StepperNavButton
                            side="left"
                            label="Vissza"
                            onClick={() => navigate("/Meretvetel")}
                        />
                        <StepperNavButton
                            side="right"
                            label="Tovább"
                            onClick={() => handleSaveAndNavigate("/Emeles")}
                            disabled={!allFilled || loading}
                        />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default MeretvetelSzaras;
